import { AbstractControl } from "@angular/forms";

export function spanishPersonalNumberValidator(control: AbstractControl): { format?: boolean } | null {
	// The following NIF/NIE personal identification number algorithm is based on the following URL =>
	// https://www.ordenacionjuego.es/en/calculo-digito-control
	// Example NIF number => 66059598X
	// Example NIE number => Y2116903C
	// Example special NIE (starting with X0) number => X00639726G

	// If a character is present at the start (position 0) of the number then it is swapped to a number provided in the dictionary
	// Currently (09.04.2020) the characters are swapped in the following:
	// X => 0
	// Y => 1
	// Z => 2

	// Validation characters dictionary
	// Characters dictionary => const validationCharacterMap = "TRWAGMYFPDXBNJZSQVHLCKE";
	// Values are assigned to characters in a growing order starting from 0
	// Example (T => 0), (R => 1), (W => 2), ...

	// An example: Y2116903C => 12116903C
	// The last character in the number is ignored in the calculation and removed for checking => 12116903
	// The number is divided by 23 => (12116903 / 23) => 526821,87
	// The remainder is removed 526821,87 => 526821
	// The number is then multiplied by 23 => (526821 * 23) => 12116883
	// The calculated number is subtracted from the provided number => (12116903 - 12116883) = 20
	// Doing a check on the dictionary the number 20 position is character C which makes this a valid NIE number

	// Test spanish personal codes start with a [A-W] character and are meant for testing purposes
	// The first [A-W] character is removed when performing validation
	// Checksum calculation is done as regular for validation
	// Example test NIF spanish personal code => A66059598X

	const controlValue = control?.value ? control.value.toString().toUpperCase().replace(/\s/g, "") : null;

	if (!controlValue) {
		return null;
	}

	// Validation characters dictionary
	// Values are assigned to characters in a growing order starting from 0
	// Example (T => 0), (R => 1), (W => 2), ...
	const validationCharacterMap = "TRWAGMYFPDXBNJZSQVHLCKE";
	const nifRegex = /^[0-9]{8}[A-Z]$/i;
	const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/i;
	// Special NIE numbers start with X0 which makes them a 10 character number
	// Regular NIF/NIE numbers are 9 characters
	// Calculation is done in the same way as written above in the description
	const nieSpecialRegex = /^(X0)[0-9]{7}([A-Z])$/i;

	// The below regex patterns and regex test are meant for test spanish personal codes
	// A first character from [A-W] alphabetical order is added to the start of the personal code
	// If the test personal tax code passes the regex test, the first character is removed
	const testNifRegex = /^[A-W][0-9]{8}[A-Z]$/i;
	const testNieRegex = /^[A-W][XYZ][0-9]{7}[A-Z]$/i;
	const testNieSpecialRegex = /^[A-W](X0)[0-9]{7}([A-Z])$/i;

	const taxCode =
		testNifRegex.test(controlValue) || testNieRegex.test(controlValue) || testNieSpecialRegex.test(controlValue)
			? controlValue.substring(1)
			: controlValue;

	if (!nifRegex.test(taxCode) && !nieRegex.test(taxCode) && !nieSpecialRegex.test(taxCode)) {
		return { format: false };
	}

	const checksumTaxCode = taxCode.replace(/^[X]/, "0").replace(/^[Y]/, "1").replace(/^[Z]/, "2");

	const checksumCharacter = taxCode.substring(taxCode.length - 1);
	const charIndex = parseInt(checksumTaxCode.slice(0, -1), 10) % 23;

	if (validationCharacterMap.charAt(charIndex) === checksumCharacter) {
		return null;
	}

	return { format: false };
}
